import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { AceEditor } from 'modules/aceEditor/AceEditorWrapper';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { WORKFLOW_ACTIONS } from 'modules/workflows/constants';
import { getIsNodeEditorReadOnly, updateNodeData } from 'modules/workflows/ducks';
import { useDisplayCloneErrors } from 'modules/workflows/hooks/useDisplayCloneErrors';
import { ChangeContactFieldNodeData } from 'modules/workflows/types';
import { CHECKBOX_STATUS, Checkbox } from 'ui/checkbox';
import { Link } from 'ui/links';
import { Label, colors, insetShadow, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';

import { StyledSubtitle } from '../../EditorBody.styled';
import { EditorBaseProps } from '../../types';

import { UpdateUserActions } from './UpdateUserActions';
import { UpdateViaWebhook } from './UpdateViaWebhook';

/* Styled components */
const FormSection = styled.div`
  margin-bottom: ${pxToRem(24)};
`;

export const StyledFormLabel = styled(Label)`
  margin-bottom: ${spacing.half};
`;

export const AceEditorWrapper = styled.div`
  > div {
    ${insetShadow}
    border: 1px solid ${colors.grey15};
    border-radius: ${pxToRem(4)};
  }
`;

export const CheckboxContainer = styled(FormSection)`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

const StyledDivider = styled.hr`
  border-color: ${colors.grey15};
  width: 100%;
  margin-top: 0;
  margin-bottom: ${pxToRem(24)};
`;

// Returns a comparable nodeData object
export const getChangeUserFieldEditorNodeDataToBeCompared = (
  data: Pick<
    ChangeContactFieldNodeData,
    'nodeClass' | 'dataFields' | 'mergeNestedObjects' | 'endpoint' | 'authToken'
  >,
) => ({
  nodeClass: data?.nodeClass ?? undefined,
  dataFields: data?.dataFields ?? undefined,
  mergeNestedObjects: data?.mergeNestedObjects ?? undefined,
  endpoint: data?.endpoint ?? undefined,
  authToken: data?.authToken ?? undefined,
});

export const EDITOR_TEST_ID = 'update-user-editor';
export const MERGE_OBJECTS = 'merge-objects';

export type ChangeUserFieldEditorProps = EditorBaseProps<ChangeContactFieldNodeData>;

export const ChangeUserFieldEditor = ({
  nodeData,
  clonedFromNodeData,
  nodeId,
  ...rest
}: ChangeUserFieldEditorProps) => {
  const [dataFields, setDataFields] = useState(JSON.stringify(nodeData?.dataFields));
  const [mergeNestedObjects, setMergeNestedObjects] = useState(nodeData?.mergeNestedObjects);
  const [endpoint, setEndpoint] = useState(nodeData?.endpoint);
  const [authToken, setAuthToken] = useState(nodeData?.authToken);
  const [updateAction, setUpdateAction] = useState(nodeData?.nodeClass);
  const isReadOnly = useSelector(getIsNodeEditorReadOnly);

  const dispatch = useDispatch();

  const shouldUpdateViaWebhook = updateAction === WORKFLOW_ACTIONS.UPDATE_CONTACT_WEBHOOK;
  const shouldUpdateViaJson = updateAction === WORKFLOW_ACTIONS.CHANGE_CONTACT_FIELD;

  const { isCloneWarningBannerShown, cloneErrors, cloneDisplayValues } = useDisplayCloneErrors({
    clonedFromNodeData,
    fieldsToValidate: {
      endpoint: {
        // checking for type allows empty strings to be valid
        // if user backspaces, for example
        isValid: typeof endpoint === 'string',
      },
      authToken: {
        isValid: typeof authToken === 'string',
      },
    },
    nodeClass: updateAction,
  });

  const getUpdatedJsonFieldsNodeData = (): Pick<
    ChangeContactFieldNodeData,
    'dataFields' | 'endpoint' | 'authToken'
  > => {
    let parsedDataFields = null;

    // stop-gap measure to stop json parsing from blowing up the page
    // we will do actual validation later
    try {
      parsedDataFields = JSON.parse(dataFields);
    } catch (error) {
      // when editor is empty we want to return nothing
      if (!dataFields) {
        parsedDataFields = undefined;
      } else {
        // go back to the previous value if json parsing blows up
        parsedDataFields = nodeData?.dataFields;
      }
    }

    return {
      dataFields: parsedDataFields,
      endpoint: undefined,
      authToken: undefined,
    };
  };

  const getUpdatedNodeData = () => {
    const { nodeSuperClass } = nodeData as ChangeContactFieldNodeData;
    const webhookData = {
      endpoint,
      authToken,
      dataFields: undefined,
    };

    return {
      // changing fields
      ...(shouldUpdateViaWebhook ? webhookData : getUpdatedJsonFieldsNodeData()),
      mergeNestedObjects,
      // fields necessary for types
      nodeClass: updateAction,
      nodeSuperClass,
    };
  };
  const handleUpdate = () => {
    const updatedNodeData = getUpdatedNodeData();
    dispatch(updateNodeData(updatedNodeData, nodeId));
  };

  const initialNodeData = useMemo(
    () => getChangeUserFieldEditorNodeDataToBeCompared(nodeData),
    [nodeData],
  );

  const updatedNodeData = getChangeUserFieldEditorNodeDataToBeCompared(getUpdatedNodeData());
  return (
    <NodeEditor
      {...rest}
      data-test={EDITOR_TEST_ID}
      onUpdate={handleUpdate}
      /** These are used to check if node has changes */
      initialNodeData={initialNodeData}
      updatedNodeData={updatedNodeData}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
    >
      <FormSection>
        <UpdateUserActions
          updateAction={updateAction}
          setUpdateAction={setUpdateAction}
          isReadOnly={isReadOnly}
        />
      </FormSection>

      <CheckboxContainer>
        <Checkbox
          onClick={() => {
            setMergeNestedObjects(!mergeNestedObjects);
          }}
          data-test={MERGE_OBJECTS}
          status={mergeNestedObjects ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
          isDisabled={isReadOnly}
        />

        <StyledSubtitle id="basic-label">
          Merge top-level objects instead of overwriting.
        </StyledSubtitle>
        <Link
          hasOpenNewTabIcon
          rel="noreferrer noopener"
          target="_blank"
          href="https://support.iterable.com/hc/articles/17743524980756"
        >
          Learn more
        </Link>
      </CheckboxContainer>

      <StyledDivider />

      {shouldUpdateViaJson && (
        <FormSection>
          <StyledFormLabel>Enter some data (in JSON)</StyledFormLabel>
          <AceEditorWrapper>
            <AceEditor
              mode="json"
              height={'300'}
              onChange={(value) => setDataFields(value)}
              wrapEnabled={true}
              value={dataFields}
              readOnly={isReadOnly}
            />
          </AceEditorWrapper>
        </FormSection>
      )}

      {shouldUpdateViaWebhook && (
        <UpdateViaWebhook
          isReadOnly={isReadOnly}
          setEndpoint={setEndpoint}
          setAuthToken={setAuthToken}
          values={{
            endpoint,
            authToken,
          }}
          cloneErrors={cloneErrors}
          cloneDisplayValues={cloneDisplayValues}
        />
      )}
    </NodeEditor>
  );
};
